import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Radio, AlertCircle, Camera, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../lib/utils";

export const PatrolLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("credentials"); // credentials | capture
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate credentials first
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });
      
      const { token, user } = response.data;
      
      // Si el usuario debe cambiar la contraseña, redirigir a la página de cambio
      if (user.must_change_password) {
        localStorage.setItem("token", token);
        toast.info("Debes cambiar tu contraseña temporal antes de continuar");
        window.location.href = "/change-password";
        return;
      }
      
      if (user.role !== "vigilante") {
        // Non-vigilante users go directly to dashboard
        localStorage.setItem("token", token);
        toast.info("Redirigiendo al panel de administración...");
        window.location.href = "/dashboard";
        return;
      }
      
      // For vigilantes, move to capture step
      setTempToken(token);
      setTempUser(user);
      setStep("capture");
      toast.success("Credenciales verificadas. Capturando ubicación...");
      
      // Auto-capture location con un pequeño delay para asegurar que el componente esté montado
      setTimeout(() => {
        captureLocationAuto();
      }, 500);
      
    } catch (err) {
      const message = err.response?.data?.detail || "Error al iniciar sesión";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const captureLocationAuto = useCallback(() => {
    if (!navigator.geolocation) {
      // Si no hay geolocalización, intentar directamente con IP
      setGettingLocation(true);
      fetch("https://ipapi.co/json/")
        .then(response => response.json())
        .then(data => {
          if (data.latitude && data.longitude) {
            setLocation({
              latitude: parseFloat(data.latitude),
              longitude: parseFloat(data.longitude),
            });
            setGettingLocation(false);
            toast.success("Ubicación aproximada obtenida (basada en IP)");
          } else {
            setGettingLocation(false);
            // No mostrar error, el usuario puede continuar con ubicación por defecto
          }
        })
        .catch(() => {
          setGettingLocation(false);
          // No mostrar error, el usuario puede continuar con ubicación por defecto
        });
      return;
    }

    setGettingLocation(true);
    
    // Primero intentamos con configuración menos estricta (más rápida y confiable)
    // Esto evita problemas de timeout con alta precisión
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setGettingLocation(false);
        toast.success("Ubicación capturada automáticamente");
      },
      (error) => {
        // Si falla, intentamos con alta precisión
        console.log("First attempt (low accuracy) failed, retrying with high accuracy:", error);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setGettingLocation(false);
            toast.success("Ubicación capturada automáticamente");
          },
          async (retryError) => {
            // Si falla también, intentamos con geolocalización por IP como fallback
            console.log("Second attempt (high accuracy) failed, trying IP geolocation:", retryError);
            setGettingLocation(true); // Mantener el estado de carga mientras intentamos IP
            try {
              const response = await fetch("https://ipapi.co/json/");
              const data = await response.json();
              if (data.latitude && data.longitude) {
                setLocation({
                  latitude: parseFloat(data.latitude),
                  longitude: parseFloat(data.longitude),
                });
                setGettingLocation(false);
                toast.success("Ubicación aproximada obtenida (basada en IP)");
                return;
              }
            } catch (ipError) {
              console.error("IP geolocation error:", ipError);
            }
            
            // Si todo falla, no mostrar error (el usuario puede continuar con ubicación por defecto)
            setGettingLocation(false);
            // No mostrar toast de error, ya que el usuario puede continuar con ubicación por defecto
            console.log("No se pudo obtener ubicación GPS ni por IP, se usará ubicación por defecto al completar el registro");
          },
          { 
            enableHighAccuracy: true,
            timeout: 25000, // 25 segundos
            maximumAge: 0 // No usar caché para alta precisión
          }
        );
      },
      { 
        enableHighAccuracy: false, // Empezar con menos precisión pero más rápido
        timeout: 15000, // 15 segundos
        maximumAge: 60000 // Permite usar ubicación en caché de hasta 1 minuto
      }
    );
  }, []);

  const startCamera = useCallback(async () => {
    try {
      // First stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      
      streamRef.current = stream;
      setCapturing(true);
      
      // Small delay to ensure state is updated and video element exists
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
      
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("Error al acceder a la cámara: " + err.message);
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      toast.error("Video no disponible");
      return;
    }
    
    // Use actual video dimensions or fallback
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    
    if (width === 0 || height === 0) {
      toast.error("Espera a que la cámara esté lista");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);
    
    const photoData = canvas.toDataURL("image/jpeg", 0.7);
    setPhoto(photoData);
    
    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCapturing(false);
    toast.success("Foto capturada");
  }, []);

  const retakePhoto = useCallback(() => {
    setPhoto(null);
    startCamera();
  }, [startCamera]);

  const useDefaultLocation = () => {
    // Ubicación por defecto: República Dominicana (Santo Domingo)
    setLocation({
      latitude: 18.4861,
      longitude: -69.9312,
    });
    toast.info("Usando ubicación por defecto (Santo Domingo, República Dominicana)");
  };

  const completeLogin = async () => {
    if (!photo) {
      toast.error("Debes tomar una foto");
      return;
    }

    // Si no hay ubicación, usar ubicación por defecto
    const finalLocation = location || {
      latitude: 18.4861, // Santo Domingo, República Dominicana
      longitude: -69.9312,
    };

    setLoading(true);
    try {
      // Save login record
      await axios.post(
        `${API_URL}/guard/login-record`,
        {
          latitude: finalLocation.latitude,
          longitude: finalLocation.longitude,
          photo_base64: photo,
        },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      // Complete login
      localStorage.setItem("token", tempToken);
      toast.success(`Bienvenido, ${tempUser.name}`);
      navigate("/patrol");
      
    } catch (err) {
      toast.error("Error al registrar entrada: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Cleanup camera on unmount
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCapturing(false);
  }, []);

  if (step === "capture") {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        {/* Header */}
        <header className="p-6 text-center">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Shield className="h-7 w-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="font-heading text-xl font-bold text-white">Sentinel Pro</h1>
              <p className="text-xs text-slate-400">Registro de Entrada</p>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md space-y-6">
            {/* Welcome */}
            <div className="text-center mb-6">
              <h2 className="text-white text-xl font-bold">¡Hola, {tempUser?.name}!</h2>
              <p className="text-slate-400 text-sm mt-1">
                Completa tu registro de entrada
              </p>
            </div>

            {/* Location Card */}
            <div className="bg-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-500" />
                  <span className="text-white font-medium">Ubicación</span>
                </div>
                {location && (
                  <span className="text-green-400 text-sm">✓ Capturada</span>
                )}
              </div>
              
              {gettingLocation ? (
                <div className="bg-slate-700 rounded-lg p-4 flex items-center justify-center gap-3">
                  <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
                  <span className="text-slate-300">Obteniendo ubicación...</span>
                </div>
              ) : location ? (
                <div className="bg-slate-700 rounded-lg p-3">
                  <p className="text-slate-300 text-sm font-mono">
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={captureLocationAuto}
                    className="mt-2 w-full border-slate-600 text-slate-300"
                  >
                    <MapPin className="h-3 w-3 mr-1" />
                    Actualizar Ubicación
                  </Button>
                </div>
              ) : (
                <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
                  <p className="text-slate-300 text-sm">
                    Ubicación no capturada. Puedes continuar sin GPS o usar ubicación por defecto.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={captureLocationAuto}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-600"
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      Intentar Obtener GPS
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={useDefaultLocation}
                      className="flex-1 border-orange-500 text-orange-400 hover:bg-orange-500/10"
                    >
                      Usar Ubicación por Defecto
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Photo Card */}
            <div className="bg-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-orange-500" />
                  <span className="text-white font-medium">Foto de Rostro</span>
                </div>
                {photo && (
                  <span className="text-green-400 text-sm">✓ Capturada</span>
                )}
              </div>

              {photo ? (
                <div className="space-y-3">
                  <img
                    src={photo}
                    alt="Foto capturada"
                    className="w-full rounded-lg"
                  />
                  <Button
                    onClick={retakePhoto}
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300"
                  >
                    Tomar otra foto
                  </Button>
                </div>
              ) : capturing ? (
                <div className="space-y-3">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', borderRadius: '0.5rem', backgroundColor: '#000', minHeight: '240px' }}
                  />
                  <Button
                    onClick={capturePhoto}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    data-testid="capture-photo-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Tomar Foto
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={startCamera}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="start-camera-btn"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Abrir Cámara
                </Button>
              )}
            </div>

            {/* Complete Button */}
            <Button
              onClick={completeLogin}
              disabled={!photo || loading}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 disabled:bg-slate-700"
              data-testid="complete-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                "COMPLETAR REGISTRO"
              )}
            </Button>
            {!location && (
              <p className="text-xs text-slate-400 text-center">
                Puedes continuar sin ubicación GPS. Se usará una ubicación por defecto.
              </p>
            )}

            {/* Cancel */}
            <Button
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
              onClick={() => {
                stopCamera();
                setStep("credentials");
                setTempToken(null);
                setTempUser(null);
                setLocation(null);
                setPhoto(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="p-6 text-center">
        <div className="inline-flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <div className="text-left">
            <h1 className="font-heading text-xl font-bold text-white">Sentinel Pro</h1>
            <p className="text-xs text-slate-400">Portal de Vigilante</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="bg-slate-800 rounded-2xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Radio className="h-10 w-10 text-orange-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-1">
                Acceso de Patrullaje
              </h2>
              <p className="text-slate-400 text-sm">
                Ingresa tus credenciales para comenzar
              </p>
            </div>

            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-400 rounded-lg text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  Correo Electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vigilante@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="patrol-login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="patrol-login-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-14 text-lg font-bold bg-orange-500 hover:bg-orange-600"
                disabled={loading}
                data-testid="patrol-login-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verificando...
                  </div>
                ) : (
                  "INICIAR TURNO"
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            ¿Eres administrador?{" "}
            <Link to="/login" className="text-orange-400 hover:underline">
              Accede al portal principal
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};
